import React, { useState, useEffect } from 'react';
import { Subscription } from '../types';
import { FunctionDeclaration, Type } from '@google/genai';
import { geminiProxy } from '../services/geminiProxy';

interface SubscriptionModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (subData: Omit<Subscription, 'id'>, id?: string) => void;
    categories: string[];
    subscriptionToEdit?: Subscription | null;
    ai: boolean;
    currency: string;
    textForAiProcessing: string | null;
    onAiProcessingComplete: () => void;
}

const SubscriptionModal: React.FC<SubscriptionModalProps> = ({ isOpen, onClose, onSave, categories, subscriptionToEdit, ai, currency, textForAiProcessing, onAiProcessingComplete }) => {
    
    const getInitialState = () => ({
        name: '',
        amount: '',
        category: categories.find(c => !c.toLowerCase().includes('ingreso')) || '',
        billingCycle: 'monthly' as 'monthly' | 'quarterly' | 'yearly',
        nextBillingDate: new Date().toISOString().split('T')[0],
        paymentReminder: '1week' as 'none' | '1day' | '2days' | '1week',
        cancellationDate: 'never',
        cancellationReminder: 'none' as 'none' | '2weeks' | '1month' | '2months',
        status: 'active' as 'active' | 'canceled',
    });

    const [formState, setFormState] = useState(getInitialState());
    const [isAiProcessing, setIsAiProcessing] = useState(false);

    useEffect(() => {
        if (isOpen) {
            if (subscriptionToEdit) {
                setFormState({
                    ...subscriptionToEdit,
                    amount: String(subscriptionToEdit.amount),
                });
            } else {
                 if (!textForAiProcessing) {
                    setFormState(getInitialState());
                 }
            }
        }
    }, [subscriptionToEdit, isOpen, categories]);

     const processTextWithAI = async (text: string) => {
        if (!ai) return;
        setIsAiProcessing(true);
        
        try {
            const today = new Date().toISOString().split('T')[0];
            const availableCategories = categories.filter(c => c !== "Desconocida" && !c.toLowerCase().includes('ingreso')).join(', ');

            const registerSubscriptionFunction: FunctionDeclaration = {
                name: 'registerSubscription',
                description: `Extrae y estructura la información de una suscripción de pago recurrente a partir de una frase. La fecha de hoy es ${today}. El objetivo es rellenar campos de un formulario, no poner toda la frase en un solo campo.`,
                parameters: {
                    type: Type.OBJECT,
                    properties: {
                        name: {
                            type: Type.STRING,
                            description: 'El nombre conciso del servicio. Ejemplo: Si la frase es "Suscripción a Netflix Premium", el nombre es "Netflix Premium".'
                        },
                        amount: {
                            type: Type.NUMBER,
                            description: 'El importe numérico del pago. Ejemplo: Si la frase es "pago de 12,99 al mes", el importe es 12.99.'
                        },
                        billingCycle: {
                            type: Type.STRING,
                            enum: ['monthly', 'quarterly', 'yearly'],
                            description: 'La frecuencia del pago. "mensual" para "al mes", "trimestral" para "al trimestre", "anual" para "al año".'
                        },
                        nextBillingDate: {
                            type: Type.STRING,
                            description: `La próxima fecha de cobro en formato AAAA-MM-DD. Si el usuario dice "mañana" o "el día 15", calcula la fecha correcta. Si no se especifica, usa la fecha de hoy: ${today}.`
                        },
                        category: {
                            type: Type.STRING,
                            description: `La categoría más apropiada de esta lista: [${availableCategories}].`
                        },
                        paymentReminder: {
                            type: Type.STRING,
                            enum: ['none', '1day', '2days', '1week'],
                            description: 'Recordatorio ANTES DEL PAGO. Extraer si el usuario lo menciona (ej: "avísame 2 días antes"). Por defecto es "1week".'
                        },
                        cancellationDate: {
                            type: Type.STRING,
                            description: 'Opcional. Fecha de cancelación futura en formato AAAA-MM-DD. Si el usuario no lo menciona, se omite.'
                        },
                        cancellationReminder: {
                            type: Type.STRING,
                            enum: ['none', '2weeks', '1month', '2months'],
                            description: 'Opcional. Recordatorio ANTES DE LA CANCELACIÓN. Extraer si el usuario lo menciona (ej: "recuérdame cancelar con un mes de antelación").'
                        }
                    },
                    required: ['name', 'amount', 'billingCycle', 'category'],
                },
            };
            
            const response = await geminiProxy.generateContent(
                `Analiza este texto para registrar una suscripción: "${text}"`,
                { tools: [{ functionDeclarations: [registerSubscriptionFunction] }] },
            );

            const functionCall = response.functionCalls?.[0];

            if (functionCall?.name === 'registerSubscription' && functionCall.args) {
                const args = functionCall.args;
                setFormState({
                    name: args.name as string || '',
                    amount: String(args.amount as number || ''),
                    category: args.category as string || categories[0],
                    billingCycle: args.billingCycle as 'monthly' | 'quarterly' | 'yearly' || 'monthly',
                    nextBillingDate: (args.nextBillingDate as string) || today,
                    paymentReminder: args.paymentReminder as 'none' | '1day' | '2days' | '1week' || '1week',
                    cancellationDate: (args.cancellationDate as string) || 'never',
                    cancellationReminder: args.cancellationReminder as 'none' | '2weeks' | '1month' | '2months' || 'none',
                    status: 'active',
                });
            } else {
                setFormState(prev => ({ ...prev, name: text }));
            }
        } catch (error) {
            console.error("Error processing subscription text with AI:", error);
            setFormState(prev => ({...prev, name: text }));
        } finally {
            setIsAiProcessing(false);
            onAiProcessingComplete();
        }
    };
    
    useEffect(() => {
        if (isOpen && textForAiProcessing && ai) {
            processTextWithAI(textForAiProcessing);
        }
    }, [isOpen, textForAiProcessing, ai]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormState(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const numericAmount = parseFloat(formState.amount);
        if (!formState.name || isNaN(numericAmount) || numericAmount <= 0 || !formState.category) {
            alert('Por favor, completa el nombre, el importe y la categoría.');
            return;
        }
        
        onSave({ ...formState, amount: numericAmount }, subscriptionToEdit?.id);
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-70 z-50 flex justify-center items-center" onClick={onClose}>
            <div className="bg-light-surface dark:bg-surface rounded-lg shadow-xl p-8 w-[95%] sm:w-full max-w-lg border border-light-border dark:border-border relative" onClick={e => e.stopPropagation()}>
                {isAiProcessing && (
                    <div className="absolute inset-0 bg-black bg-opacity-50 flex flex-col justify-center items-center z-10 rounded-lg">
                        <div className="w-12 h-12 border-4 border-white rounded-full border-t-transparent animate-spin"></div>
                        <p className="text-white mt-4 font-semibold">La IA está procesando...</p>
                    </div>
                )}
                <h2 className="text-2xl font-bold text-light-text-primary dark:text-text-primary mb-6">{subscriptionToEdit ? 'Editar Suscripción' : 'Nueva Suscripción'}</h2>
                <form onSubmit={handleSubmit} className="space-y-4">
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <label htmlFor="name" className="block text-sm font-medium text-light-text-secondary dark:text-text-secondary mb-1">Nombre</label>
                            <input type="text" name="name" value={formState.name} onChange={handleInputChange} required className="w-full bg-light-background dark:bg-background border rounded px-3 py-2" placeholder="Ej: Netflix, Spotify" />
                        </div>
                        <div>
                             <label htmlFor="category" className="block text-sm font-medium text-light-text-secondary dark:text-text-secondary mb-1">Categoría</label>
                             <select name="category" value={formState.category} onChange={handleInputChange} required className="w-full bg-light-background dark:bg-background border rounded px-3 py-2">
                                <option value="" disabled>Selecciona una</option>
                                {categories.filter(c => !c.toLowerCase().includes('ingreso')).map(c => <option key={c} value={c}>{c}</option>)}
                            </select>
                        </div>
                    </div>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <label htmlFor="amount" className="block text-sm font-medium text-light-text-secondary dark:text-text-secondary mb-1">Coste ({currency})</label>
                            <input type="number" name="amount" value={formState.amount} onChange={handleInputChange} step="0.01" min="0" required className="w-full bg-light-background dark:bg-background border rounded px-3 py-2" placeholder="12.99" />
                        </div>
                         <div>
                            <label htmlFor="billingCycle" className="block text-sm font-medium text-light-text-secondary dark:text-text-secondary mb-1">Ciclo de Cobro</label>
                            <select name="billingCycle" value={formState.billingCycle} onChange={handleInputChange} className="w-full bg-light-background dark:bg-background border rounded px-3 py-2">
                                <option value="monthly">Mensual</option>
                                <option value="quarterly">Trimestral</option>
                                <option value="yearly">Anual</option>
                            </select>
                        </div>
                    </div>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                             <label htmlFor="nextBillingDate" className="block text-sm font-medium text-light-text-secondary dark:text-text-secondary mb-1">Próximo Cobro</label>
                            <input type="date" name="nextBillingDate" value={formState.nextBillingDate} onChange={handleInputChange} required className="w-full bg-light-background dark:bg-background border rounded px-3 py-2" />
                        </div>
                        <div>
                            <label htmlFor="paymentReminder" className="block text-sm font-medium text-light-text-secondary dark:text-text-secondary mb-1">Aviso de Pago</label>
                            <select name="paymentReminder" value={formState.paymentReminder} onChange={handleInputChange} className="w-full bg-light-background dark:bg-background border rounded px-3 py-2">
                                <option value="none">No avisar</option>
                                <option value="1day">1 día antes</option>
                                <option value="2days">2 días antes</option>
                                <option value="1week">1 semana antes</option>
                            </select>
                        </div>
                    </div>

                    <div className="p-4 bg-gray-100 dark:bg-gray-800/50 rounded-lg border border-light-border dark:border-border">
                        <h4 className="font-semibold mb-3 text-light-text-primary dark:text-text-primary">Plan de Cancelación (Opcional)</h4>
                         <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <label htmlFor="cancellationDate" className="block text-sm font-medium text-light-text-secondary dark:text-text-secondary mb-1">Fecha de Cancelación</label>
                                <input type="date" name="cancellationDate" value={formState.cancellationDate === 'never' ? '' : formState.cancellationDate} onChange={(e) => setFormState(p => ({...p, cancellationDate: e.target.value || 'never'}))} className="w-full bg-light-background dark:bg-background border rounded px-3 py-2" />
                                <button type="button" onClick={() => setFormState(p => ({...p, cancellationDate: 'never'}))} className={`text-xs mt-1 ${formState.cancellationDate === 'never' ? 'text-primary font-bold' : 'text-light-text-secondary dark:text-text-secondary'}`}>Nunca</button>
                            </div>
                            <div>
                                <label htmlFor="cancellationReminder" className="block text-sm font-medium text-light-text-secondary dark:text-text-secondary mb-1">Aviso de Cancelación</label>
                                <select name="cancellationReminder" value={formState.cancellationReminder} onChange={handleInputChange} className="w-full bg-light-background dark:bg-background border rounded px-3 py-2">
                                    <option value="none">No avisar</option>
                                    <option value="2weeks">2 semanas antes</option>
                                    <option value="1month">1 mes antes</option>
                                    <option value="2months">2 meses antes</option>
                                </select>
                            </div>
                         </div>
                    </div>
                    
                    <div>
                        <label htmlFor="status" className="block text-sm font-medium text-light-text-secondary dark:text-text-secondary mb-1">Estado</label>
                        <select name="status" value={formState.status} onChange={handleInputChange} className="w-full bg-light-background dark:bg-background border rounded px-3 py-2">
                            <option value="active">Activa</option>
                            <option value="canceled">Cancelada</option>
                        </select>
                    </div>

                    <div className="flex justify-end gap-4 pt-4">
                        <button type="button" onClick={onClose} className="px-4 py-2 rounded-md bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500">Cancelar</button>
                        <button type="submit" className="px-4 py-2 rounded-md bg-primary hover:bg-blue-600 text-white font-semibold">
                            {subscriptionToEdit ? 'Guardar Cambios' : 'Guardar Suscripción'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default SubscriptionModal;